import { IComponentOptions, IAngularEvent, IHttpResponse, IScope, IPromise } from "angular";
import { DataSourceService } from "@gtmhub/datasources";
import { IDataSource } from "@gtmhub/datasources/models/models";
import { isDatasourceExistsResponse, isDefaultDatasourceEntityExistsResponse } from "@gtmhub/datasources/utils";
import { IEntityStatusChangedArgs } from "@gtmhub/entities/models/models";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IDataSourceEntity } from "@gtmhub/insights";
import { LookerInsightSelectorService } from "../services/looker-insight-selector-service";
import { DatasourceConfig, DataSourceType, ILookerBuildInsightContext, ILookerConnectionData, ILookerEntity } from "../models/models";
import { getEntityConfig } from "../utils";
import { configUIError } from "../errors/errors";

export interface LookerInsightBuilderCtrlScope extends IScope {
  datasourceConfig: DatasourceConfig;
  connections: ILookerConnectionData[];
  dashboards: ILookerEntity[];
  tiles: ILookerEntity[];
  looks: ILookerEntity[];
  indicators: {
    loading: boolean;
    syncing: boolean;
  };
  createDataSource(datasourceConfig: DatasourceConfig): void;
}

export class LookerInsightBuilderCtrl {
  public static $inject = ["$scope", "LookerInsightSelectorService", "DataSourceService", "UIErrorHandlingService"];

  // ---- Bindings ---- //
  close: (args: { $value: IDataSourceEntity }) => void;
  dismiss: () => void;
  resolve: ILookerBuildInsightContext;

  private datasource: IDataSource;
  private initialized = false;

  constructor(
    private $scope: LookerInsightBuilderCtrlScope,
    private insightSelectorService: LookerInsightSelectorService,
    private dataSourceService: DataSourceService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {}

  public $onInit(): void {
    this.$scope.indicators = {
      loading: false,
      syncing: false,
    };

    this.$scope.datasourceConfig = {
      type: "dashboard",
      connectionId: null,
    };

    this.$scope.connections = this.resolve.connections;

    if (this.resolve.connections.length === 1) {
      this.$scope.datasourceConfig.connectionId = this.resolve.connections[0].id.toString();
    }

    this.$scope.createDataSource = (config: DatasourceConfig) => this.createDataSource(config);

    this.$scope.$watch("datasourceConfig.connectionId", (connectionId: string) => this.handleConnectionIdChange(connectionId));
    this.$scope.$watch("datasourceConfig.type", (type: DataSourceType) => this.handleTypeChange(type));
    this.$scope.$watch("datasourceConfig.dashboardId", (dashboardId: string) => this.handleDashboardIdChange(dashboardId));

    this.$scope.$on("entityStatusChanged", this.handleEntityStatusChanged);
  }

  private handleEntityStatusChanged = (evt: IAngularEvent, args: IEntityStatusChangedArgs): void => {
    if (!this.$scope.indicators.syncing || !this.datasource || this.datasource.id !== args.dataSourceId) {
      return;
    }

    switch (args.status) {
      case "connected":
        return this.close({
          $value: {
            entity: args.entityName,
            entityTitle: this.datasource.name,
            dataSource: this.datasource.name,
            dataSourceImg: this.resolve.blueprintName,
          } as IDataSourceEntity,
        });
      case "syncingProblem":
        return this.uiErrorHandlingService.handleIndicator({ body: args.failureReason }, { indicator: "syncing", inline: true });
    }
  };

  private handleConnectionIdChange(connectionId: string) {
    if (!connectionId) {
      return;
    }

    this.$scope.dashboards = [];
    this.$scope.looks = [];
    this.$scope.tiles = [];

    this.$scope.datasourceConfig.dashboardId = "";
    this.$scope.datasourceConfig.lookId = "";
    this.$scope.datasourceConfig.tileId = "";
    this.$scope.datasourceConfig.type = "dashboard";

    this.getDashboards().then(() => {
      this.initialized = true;
    });
  }

  private handleTypeChange(type: DataSourceType): void {
    if (!type || !this.$scope.datasourceConfig.connectionId || !this.initialized) {
      return;
    }

    this.$scope.dashboards = [];
    this.$scope.looks = [];
    this.$scope.tiles = [];

    this.$scope.datasourceConfig.dashboardId = "";
    this.$scope.datasourceConfig.lookId = "";
    this.$scope.datasourceConfig.tileId = "";

    if (type === "dashboard") {
      this.getDashboards();
    } else if (type === "look") {
      this.getLooks();
    }
  }

  private handleDashboardIdChange(dashboardId: string): void {
    if (!dashboardId || !this.$scope.datasourceConfig.connectionId) {
      return;
    }

    this.$scope.looks = [];
    this.$scope.tiles = [];

    this.$scope.datasourceConfig.lookId = "";
    this.$scope.datasourceConfig.tileId = "";

    this.$scope.indicators.loading = true;
    this.insightSelectorService.getTiles(this.$scope.datasourceConfig.connectionId, dashboardId).then(
      (tiles: ILookerEntity[]) => {
        this.$scope.tiles = tiles;
        this.$scope.indicators.loading = false;
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true })
    );
  }

  private getDashboards(): IPromise<void> {
    this.$scope.indicators.loading = true;
    return this.insightSelectorService.getDashboards(this.$scope.datasourceConfig.connectionId).then(
      (dashboards: ILookerEntity[]) => {
        this.$scope.dashboards = dashboards;
        this.$scope.indicators.loading = false;
      },
      (error) => {
        this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true });
      }
    );
  }

  private getLooks(): IPromise<void> {
    this.$scope.indicators.loading = true;
    return this.insightSelectorService.getLooks(this.$scope.datasourceConfig.connectionId).then(
      (looks: ILookerEntity[]) => {
        this.$scope.looks = looks;
        this.$scope.indicators.loading = false;
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true })
    );
  }

  public createDataSource(config: DatasourceConfig): IPromise<void> {
    const { connectionId, type } = config;

    this.$scope.indicators.syncing = true;

    if (!connectionId || !type) {
      this.uiErrorHandlingService.handleIndicator(configUIError(config, { looks: this.$scope.looks, tiles: this.$scope.tiles }), { indicator: "syncing", inline: true });
      return;
    }

    const entityConfig = getEntityConfig(config, this.$scope.tiles, this.$scope.looks);
    if (!entityConfig) {
      this.uiErrorHandlingService.handleIndicator(configUIError(config, { looks: this.$scope.looks, tiles: this.$scope.tiles }), { indicator: "syncing", inline: true });
      return;
    }

    return this.dataSourceService
      .createDataSource({
        name: entityConfig.name,
        defaultEntityName: entityConfig.defaultEntityName,
        syncSchedule: "hourly",
        key: entityConfig.key,
        connectorId: config.connectionId,
      })
      .then(
        (datasource: IDataSource): void => {
          this.datasource = datasource;
        },
        (error: IHttpResponse<unknown>): void => {
          if (!isDatasourceExistsResponse(error)) {
            return this.uiErrorHandlingService.handleIndicator(error, { indicator: "syncing", inline: true });
          }

          // Assign the local scope `datasource` prop so that either `dataSourceService.createEntity`
          // error handler or the `handleEntityStatusChanged` callback can further process the request.
          this.datasource = error.data.data;
          // Create the entity after that.
          this.dataSourceService.createEntity(error.data.resourceId).catch((error) => {
            if (!isDefaultDatasourceEntityExistsResponse(error)) {
              return this.uiErrorHandlingService.handleIndicator(error, { indicator: "syncing", inline: true });
            }

            return this.close({
              $value: {
                entity: error.data.data.name,
                entityTitle: this.datasource.name,
                dataSource: this.datasource.name,
                dataSourceImg: this.resolve.blueprintName,
              } as IDataSourceEntity,
            });
          });
        }
      );
  }
}

export const LookerInsightBuilderComponent: IComponentOptions = {
  controller: LookerInsightBuilderCtrl,
  templateUrl: require("./looker-insight-builder.jade"),
  bindings: {
    close: "&",
    dismiss: "&",
    resolve: "<",
  },
};
